// src/components/business/UpsellEngineSection.tsx
import React, { useState } from 'react';
import { 
  Sparkles, Zap, Smartphone, Mail, Clock, TrendingUp, 
  Plus, Target, CheckCircle, ArrowRight, Loader2, MessageSquare
} from 'lucide-react';

export default function UpsellEngineSection() {
  const [isCreating, setIsCreating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  // États du formulaire de campagne
  const [campaignName, setCampaignName] = campaignNameState("Offre Surclassement VIP");
  const [trigger, setTrigger] = useState("pre_arrival"); // post_booking, pre_arrival, in_stay
  const [channel, setChannel] = useState("sms"); // sms, email
  const [offerType, setOfferType] = useState("upgrade"); 
  const [message, setMessage] = useState("Bonjour {client_name} ! Votre séjour approche. Souhaitez-vous un surclassement en Suite Vue Océan pour seulement 49$/nuit ? Cliquez ici : {lien}");

  // Mock de campagnes actives pour l'effet "Wow"
  const activeCampaigns = [
    { id: 1, name: "Promo Spa - Post Réservation", trigger: "Immédiat", conversions: 24, revenue: 1450, status: "active" },
    { id: 2, name: "Late Check-out automatique", trigger: "La veille du départ", conversions: 18, revenue: 540, status: "active" }
  ];

  const handleAIGeneration = () => {
    setAiLoading(true);
    // Simulation d'un appel IA
    setTimeout(() => {
      if (offerType === 'upgrade') {
        setMessage("✨ Préparez votre arrivée, {client_name} ! Rendez votre séjour inoubliable avec un surclassement en Suite et une bouteille de champagne à votre arrivée. 🥂 Réservez votre offre exclusive ici : {lien}");
      } else if (offerType === 'spa') {
        setMessage("🧘‍♀️ Détente absolue {client_name}... Il nous reste 2 places au Spa Forfeo pour demain. Profitez de -20% sur les massages en réservant maintenant : {lien}");
      } else {
        setMessage("👋 Bonjour {client_name}, saviez-vous que vous pouviez garder votre chambre jusqu'à 15h le jour de votre départ pour 30$ ? Profitez de la grasse matinée : {lien}");
      }
      setAiLoading(false);
    }, 1500);
  };

  function campaignNameState(initial: string) {
      return useState(initial);
  }

  return (
    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-6 md:p-10 shadow-xl relative overflow-hidden animate-in fade-in">
      
      {/* Effets d'arrière-plan stylisés */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 relative z-10">
        <div>
          <h2 className="text-3xl font-black text-slate-900 flex items-center gap-3">
            <Zap className="text-yellow-500" fill="currentColor" size={32} />
            Moteur d'Upsell <span className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-transparent bg-clip-text">Auto</span>
          </h2>
          <p className="text-slate-700 mt-2 font-medium">Générez des revenus automatiques avant, pendant et après le séjour de vos clients.</p>
        </div>
        {!isCreating && (
          <button 
            onClick={() => setIsCreating(true)}
            className="mt-4 md:mt-0 bg-indigo-600 text-white px-6 py-3.5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-600 transition-all shadow-lg hover:shadow-indigo-500/30 flex items-center gap-2"
          >
            <Plus size={16} /> Nouvelle Campagne
          </button>
        )}
      </div>

      {!isCreating ? (
        <div className="space-y-8 relative z-10">
          {/* KPI CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-3xl text-slate-950 shadow-lg relative overflow-hidden group">
              <div className="absolute right-0 top-0 w-32 h-32 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all"></div>
              <TrendingUp className="text-green-400 mb-3" size={28} />
              <p className="text-slate-700 text-xs font-bold uppercase tracking-widest mb-1">Revenus Générés (30j)</p>
              <p className="text-4xl font-black">1 990 $</p>
            </div>
            <div className="bg-white border border-slate-100 p-6 rounded-3xl shadow-sm">
              <Target className="text-indigo-500 mb-3" size={28} />
              <p className="text-slate-700 text-xs font-bold uppercase tracking-widest mb-1">Taux de Conversion</p>
              <p className="text-4xl font-black text-slate-900">14.2 %</p>
            </div>
            <div className="bg-white border border-slate-100 p-6 rounded-3xl shadow-sm">
              <MessageSquare className="text-fuchsia-500 mb-3" size={28} />
              <p className="text-slate-700 text-xs font-bold uppercase tracking-widest mb-1">Messages Envoyés</p>
              <p className="text-4xl font-black text-slate-900">342</p>
            </div>
          </div>

          {/* LISTE DES CAMPAGNES */}
          <div>
            <h3 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="text-indigo-500" size={20}/> Vos campagnes actives
            </h3>
            <div className="space-y-4">
              {activeCampaigns.map(camp => (
                <div key={camp.id} className="flex flex-col md:flex-row justify-between items-center bg-slate-50 border border-slate-100 p-5 rounded-2xl hover:shadow-md transition">
                  <div className="flex items-center gap-4 w-full md:w-auto mb-4 md:mb-0">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-indigo-500">
                      <Zap size={20} />
                    </div>
                    <div>
                      <h4 className="font-black text-slate-900">{camp.name}</h4>
                      <p className="text-xs text-slate-700 font-bold flex items-center gap-1">
                        <Clock size={12} /> Déclencheur : {camp.trigger}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-8 w-full md:w-auto text-center md:text-right border-t md:border-t-0 border-slate-200 pt-4 md:pt-0">
                    <div>
                      <p className="text-xs text-slate-700 font-bold uppercase tracking-widest">Achats</p>
                      <p className="font-black text-lg text-slate-900">{camp.conversions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-700 font-bold uppercase tracking-widest">Revenus</p>
                      <p className="font-black text-lg text-green-600">+{camp.revenue}$</p>
                    </div>
                    <div className="flex items-center">
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center gap-1">
                        <CheckCircle size={10}/> Actif
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* ========================================================= */
        /* CRÉATEUR DE CAMPAGNE AVEC LIVE PREVIEW MOBILE             */
        /* ========================================================= */
        <div className="flex flex-col lg:flex-row gap-10 relative z-10 animate-in slide-in-from-right-8 duration-500">
          
          {/* COLONNE GAUCHE : Formulaire */}
          <div className="flex-1 space-y-8">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-black text-slate-900">Configurer l'offre</h3>
              <button onClick={() => setIsCreating(false)} className="text-sm font-bold text-slate-700 hover:text-slate-900">Annuler</button>
            </div>

            <div className="space-y-6">
              {/* Nom */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Nom de la campagne</label>
                <input 
                  type="text" 
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-indigo-500 transition"
                />
              </div>

              {/* Déclencheur & Offre */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Quand l'envoyer ?</label>
                  <select 
                    value={trigger}
                    onChange={(e) => setTrigger(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-indigo-500"
                  >
                    <option value="post_booking">Directement après réservation</option>
                    <option value="pre_arrival">72h avant l'arrivée</option>
                    <option value="in_stay">Le 2ème jour du séjour</option>
                    <option value="pre_checkout">La veille du départ (18h)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Que vendre ?</label>
                  <select 
                    value={offerType}
                    onChange={(e) => setOfferType(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-indigo-500"
                  >
                    <option value="upgrade">Surclassement de chambre</option>
                    <option value="spa">Soin / Spa</option>
                    <option value="restaurant">Réservation Table + Vin</option>
                    <option value="late_checkout">Départ Tardif (Late Check-out)</option>
                  </select>
                </div>
              </div>

              {/* Canal */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Canal d'envoi</label>
                <div className="flex bg-slate-50 rounded-2xl p-1 border border-slate-200 w-fit">
                  <button onClick={() => setChannel('sms')} className={`flex items-center gap-2 px-6 py-2 text-sm font-black rounded-xl transition ${channel === 'sms' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-700 hover:text-slate-600'}`}>
                    <Smartphone size={16} /> SMS
                  </button>
                  <button onClick={() => setChannel('email')} className={`flex items-center gap-2 px-6 py-2 text-sm font-black rounded-xl transition ${channel === 'email' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-700 hover:text-slate-600'}`}>
                    <Mail size={16} /> Email
                  </button>
                </div>
              </div>

              {/* Message & IA */}
              <div className="relative pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700">Message ({channel.toUpperCase()})</label>
                  <button 
                    onClick={handleAIGeneration}
                    disabled={aiLoading}
                    className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-fuchsia-600 bg-fuchsia-50 px-3 py-1.5 rounded-full hover:bg-fuchsia-100 transition"
                  >
                    {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Magie IA
                  </button>
                </div>
                <textarea 
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full h-32 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-medium text-sm text-slate-900 outline-none focus:ring-2 ring-indigo-500 resize-none leading-relaxed"
                />
                <p className="text-[10px] text-slate-700 font-bold mt-2">Variables dispo : <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-600">{"{client_name}"}</code>, <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-600">{"{lien}"}</code></p>
              </div>

              <button 
                className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-sm hover:shadow-lg hover:shadow-indigo-500/30 transition-all flex items-center justify-center gap-2 transform hover:-translate-y-1"
                onClick={() => {
                  alert("🎉 Campagne d'Upsell activée avec succès !");
                  setIsCreating(false);
                }}
              >
                Activer l'automatisation <ArrowRight size={18} />
              </button>
            </div>
          </div>

          {/* COLONNE DROITE : MOCKUP SMARTPHONE (LE "WOW") */}
          <div className="hidden lg:flex w-[350px] justify-center items-start pt-8">
            {/* Design du Smartphone */}
            <div className="w-[300px] h-[600px] bg-slate-50 rounded-[3rem] border-[10px] border-slate-200 shadow-2xl relative flex flex-col overflow-hidden">
              {/* Encoche (Notch) */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-6 bg-white rounded-b-2xl z-20"></div>
              
              {/* Écran du téléphone */}
              <div className="flex-1 bg-white relative">
                {/* Header App Message */}
                <div className="bg-slate-100/80 backdrop-blur-md pt-10 pb-3 px-4 flex items-center justify-between border-b border-slate-200 sticky top-0 z-10">
                  <div className="text-blue-500 font-bold text-sm">{"<"} Retour</div>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-black text-xs mb-1">
                      H
                    </div>
                    <span className="text-[10px] font-black text-slate-900">L'Hôtel</span>
                  </div>
                  <div className="w-12"></div> {/* Spacer */}
                </div>

                {/* Bulle de message */}
                <div className="p-4 flex flex-col gap-4 overflow-y-auto h-full pb-20">
                  <div className="text-center text-[10px] text-slate-700 font-bold mb-2">Aujourd'hui 14:30</div>
                  
                  <div className="self-start max-w-[85%] bg-slate-100 text-slate-800 p-3.5 rounded-2xl rounded-tl-sm text-sm font-medium shadow-sm animate-in fade-in slide-in-from-bottom-4">
                    {message.replace('{client_name}', 'M. Dupont').replace('{lien}', 'forfeo.com/up/123')}
                  </div>
                  
                  {/* Lien visuel (Rich link) généré automatiquement */}
                  <div className="self-start w-[85%] bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-bottom-6 delay-150">
                    <div className="h-24 bg-indigo-100 relative">
                      <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" className="w-full h-full object-cover" alt="Upsell" />
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] text-slate-700 uppercase font-black tracking-wider">Offre Privilège</p>
                      <p className="text-xs font-bold text-slate-900 mt-0.5 truncate">
                        {offerType === 'upgrade' ? 'Surclassement Suite' : offerType === 'spa' ? 'Accès Spa & Massage' : 'Départ Tardif 15h'}
                      </p>
                      <p className="text-[10px] text-indigo-600 font-bold mt-1">Cliquer pour activer →</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Barre d'accueil iOS en bas */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-1/3 h-1 bg-slate-300 rounded-full"></div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
